import type { CSSProperties, ReactNode } from 'react'
import type { StatusKind, SavedStatus, Severity } from './governanceModel'

/** StatusKind → scoped governance color. */
export const STATUS_KIND_VAR: Record<StatusKind, string> = {
  verified: 'var(--govv-verified)',
  attention: 'var(--govv-attention)',
  denied: 'var(--govv-denied)',
  neutral: 'var(--govv-neutral)',
  identity: 'var(--govv-identity)',
  secure: 'var(--govv-secure)',
  structure: 'var(--govv-structure)',
}

/** Honesty labels for the saved-status of a record or interaction. */
export const SAVED_STATUS_META: Record<SavedStatus, { label: string; tip: string; varName: string }> = {
  saved_durable: {
    label: 'Durable', varName: 'var(--govv-verified)',
    tip: 'Persisted server-side (read from a real durable source).',
  },
  saved_local_session: {
    label: 'Local session', varName: 'var(--govv-attention)',
    tip: 'Exists only in this browser session — not written to a server.',
  },
  simulated: {
    label: 'Simulated', varName: 'var(--govv-structure)',
    tip: 'A labeled what-if — no real state changed.',
  },
}

export const SEVERITY_VAR: Record<Severity, string> = {
  info: 'var(--govv-neutral)',
  low: 'var(--govv-structure)',
  medium: 'var(--govv-attention)',
  high: 'var(--govv-denied)',
  critical: 'var(--govv-denied)',
}

export function StatusPill({ kind, label }: { kind: StatusKind; label: string }) {
  return (
    <span className="olv-gov-pill" style={{ color: STATUS_KIND_VAR[kind] }}>{label}</span>
  )
}

export function SavedStatusBadge({ status }: { status: SavedStatus }) {
  const meta = SAVED_STATUS_META[status]
  return (
    <span className="olv-gov-pill" style={{ color: meta.varName }} title={meta.tip}>{meta.label}</span>
  )
}

export function SeverityDot({ severity }: { severity: Severity }) {
  return (
    <span aria-label={`Severity ${severity}`} title={`Severity: ${severity}`}
      style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: SEVERITY_VAR[severity] }} />
  )
}

export interface MetricTileProps {
  label: string
  value: number
  varName: string
  active?: boolean
  onClick?: () => void
}

const metricTileStyle = (active: boolean): CSSProperties => ({
  border: `1px solid ${active ? 'var(--govv-line-hover)' : 'var(--govv-line)'}`,
  borderRadius: 'var(--govv-radius-metric)',
  background: active ? 'var(--sc-surface-3)' : 'var(--govv-panel)',
  padding: '12px 14px', textAlign: 'left', cursor: 'pointer', minWidth: 0,
})

export function MetricTile({ label, value, varName, active = false, onClick }: MetricTileProps) {
  return (
    <button
      type="button"
      className="olv-gov-card"
      data-clickable="true"
      aria-pressed={active}
      onClick={onClick}
      style={{ ...metricTileStyle(active), ['--govv-accent' as string]: varName }}
    >
      <div style={{ fontSize: 22, fontWeight: 700, color: varName, fontFamily: 'var(--sc-font-mono)' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--govv-ink-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{label}</div>
    </button>
  )
}

/** Deterministic, SSR-safe human time (no locale drift in tests). */
export function formatWhen(isoTime: string): string {
  // e.g. "Jul 10, 15:42" — parsed from the stored ISO.
  const d = new Date(isoTime)
  if (Number.isNaN(d.getTime())) return isoTime
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const mm = months[d.getUTCMonth()]
  const dd = d.getUTCDate()
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const min = String(d.getUTCMinutes()).padStart(2, '0')
  return `${mm} ${dd}, ${hh}:${min} UTC`
}

export function Panel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{
      border: '1px solid var(--govv-line)', borderRadius: 'var(--govv-radius-record)',
      background: 'var(--govv-panel)', padding: 16, ...style,
    }}>
      {children}
    </div>
  )
}
